import { useEffect } from 'react';
import { Check, ChevronDown, GripHorizontal, Minus, PhoneOutgoing } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/features/auth/hooks/use-current-user';
import { useCurrentOrganization } from '@/features/organization/hooks/use-current-organization';

import type { IWidgetDragHandlers } from '../../hooks/use-widget-drag';
import { useMyNumbers } from '../../hooks/use-my-numbers';
import { useVoiceStore } from '../../state/voice-ui.store';
import { PresenceStatusMenu } from './presence-status-menu';

function initials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
}

/**
 * Caller-ID selector — numbers where the agent is a ring target, plus
 * "Auto" (server picks a line via its fallback chain). The selection is
 * persisted in the voice store and sent as `fromIdentityId` on dial; the
 * History tab filters by it.
 */
function CallerIdMenu() {
    const selectedFromIdentityId = useVoiceStore((s) => s.selectedFromIdentityId);
    const setSelectedFromIdentityId = useVoiceStore((s) => s.setSelectedFromIdentityId);
    const { data: numbers = [], isSuccess } = useMyNumbers();

    // Once the agent's lines have loaded, a persisted caller-ID that is no
    // longer among them is stale (line un-assigned or removed) — clear it so
    // the header, dial payload, and History filter fall back to "Auto" instead
    // of silently using a number the server would reject. Gate strictly on
    // query success so a transient loading/error state never wipes a valid
    // choice.
    useEffect(() => {
        if (!isSuccess) return;
        if (selectedFromIdentityId === null) return;
        if (numbers.some((n) => n.channelIdentityId === selectedFromIdentityId)) return;
        setSelectedFromIdentityId(null);
    }, [isSuccess, numbers, selectedFromIdentityId, setSelectedFromIdentityId]);

    const selected = numbers.find((n) => n.channelIdentityId === selectedFromIdentityId) ?? null;
    const label = selected ? (selected.displayName ?? selected.identity) : 'Auto';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label="Choose caller ID"
                title={`Caller ID: ${label}`}
                className="flex min-w-0 shrink items-center gap-1 rounded-full border border-white/60 bg-white/60 px-2 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-white/80 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none dark:border-white/10 dark:bg-white/[0.06] dark:hover:bg-white/10"
            >
                <PhoneOutgoing className="size-3 shrink-0 text-text-tertiary" />
                <span className="max-w-[calc(6rem*var(--voice-scale,1))] truncate">{label}</span>
                <ChevronDown className="size-3 shrink-0 text-text-tertiary" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-1001 w-52">
                <DropdownMenuItem
                    onClick={() => setSelectedFromIdentityId(null)}
                    className="gap-2 text-xs"
                >
                    <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">Auto</p>
                        <p className="truncate text-text-tertiary">Best line for each call</p>
                    </div>
                    {!selectedFromIdentityId && (
                        <Check className="ml-auto size-3.5 shrink-0 text-text-tertiary" />
                    )}
                </DropdownMenuItem>
                {numbers.map((number) => (
                    <DropdownMenuItem
                        key={number.channelIdentityId}
                        onClick={() => setSelectedFromIdentityId(number.channelIdentityId)}
                        className="gap-2 text-xs"
                    >
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">
                                {number.displayName ?? number.identity}
                            </p>
                            {number.displayName && (
                                <p className="truncate text-text-tertiary">{number.identity}</p>
                            )}
                        </div>
                        {number.channelIdentityId === selectedFromIdentityId && (
                            <Check className="ml-auto size-3.5 shrink-0 text-text-tertiary" />
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** Idle header content — the signed-in agent + presence and caller-ID controls. */
function AgentIdentity({ canReceive, canMake }: { canReceive: boolean; canMake: boolean }) {
    const { data: me } = useCurrentUser();
    const { data: org } = useCurrentOrganization();
    const name = me ? `${me.firstName} ${me.lastName}`.trim() : '';

    return (
        <>
            <Avatar size="sm" className="shrink-0">
                {me?.avatarUrl && <AvatarImage src={me.avatarUrl} alt={name} />}
                <AvatarFallback className="text-[10px] group-data-[size=sm]/avatar:text-[10px]">
                    {me ? initials(me.firstName, me.lastName) : '?'}
                </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
                <p className="truncate text-[calc(0.75rem*var(--voice-scale,1))] leading-tight font-semibold text-text-primary">
                    {name || '—'}
                </p>
                {org?.name && (
                    <p className="truncate text-[calc(10px*var(--voice-scale,1))] leading-tight text-text-tertiary">
                        {org.name}
                    </p>
                )}
            </div>
            {/* Only receive-capable agents are inbound ring targets. */}
            {canReceive && <PresenceStatusMenu />}
            {/* Caller ID feeds dialing — needs VOICE_CALLS_MAKE (also gates the fetch). */}
            {canMake && <CallerIdMenu />}
        </>
    );
}

interface WidgetHeaderProps {
    readonly dragHandlers: IWidgetDragHandlers;
    readonly onMinimize: () => void;
    readonly canReceive: boolean;
    readonly canMake: boolean;
}

/**
 * Widget title bar (also the drag handle). Idle: agent avatar/name/company
 * plus the presence and caller-ID dropdowns. Ringing/in-call: the org line
 * the call runs on, so the agent always knows which number is engaged.
 */
export function WidgetHeader({ dragHandlers, onMinimize, canReceive, canMake }: WidgetHeaderProps) {
    const ringingQueue = useVoiceStore((s) => s.ringingQueue);
    const activeCall = useVoiceStore((s) => s.activeCall);

    // The active call owns the header; with only ringing calls, the oldest
    // one's line stands in (its queue row shows the caller).
    const line = activeCall?.channelIdentity ?? ringingQueue[0]?.channelIdentity ?? null;
    const lineLabel =
        (line ? (line.displayName ?? line.identity) : null) ??
        // No snapshot — fall back to the raw org-side number of the call.
        (activeCall
            ? activeCall.direction === 'INBOUND'
                ? activeCall.toNumber
                : activeCall.fromNumber
            : null);
    const onCall = Boolean(activeCall || ringingQueue.length > 0);

    return (
        <div
            {...dragHandlers}
            data-slot="voice-widget-header"
            className={cn(
                'flex cursor-grab touch-none items-center gap-2 border-b border-white/50 bg-white/40 py-1.5 pr-1.5 pl-2.5 select-none active:cursor-grabbing dark:border-white/[0.06] dark:bg-white/[0.03]',
            )}
        >
            <GripHorizontal className="size-3.5 shrink-0 text-text-tertiary" />
            {onCall ? (
                <span
                    title={lineLabel ?? undefined}
                    className="min-w-0 flex-1 truncate text-xs font-semibold text-text-secondary"
                >
                    {lineLabel ?? 'Voice'}
                </span>
            ) : (
                <AgentIdentity canReceive={canReceive} canMake={canMake} />
            )}
            <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={onMinimize}
                aria-label="Minimize"
                className="shrink-0 text-text-tertiary hover:text-text-primary"
            >
                <Minus className="size-4" />
            </Button>
        </div>
    );
}
